import React, { useMemo, useState } from 'react';
import { api } from '../api';
import { computeSOWCosts } from '../computeSOWCosts';
import SOWCostTable from './SOWCostTable';

const SELECT = ({ value, onChange, options }) => (
  <select value={value || ''} onChange={e => onChange(e.target.value)}>
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

function Field({ label, children }) {
  return (
    <div style={{marginBottom:10}}>
      <label style={{fontSize:11,fontWeight:600,color:'var(--text-muted)',display:'block',marginBottom:3}}>{label}</label>
      {children}
    </div>
  );
}

function NumberInput({ value, onChange, placeholder }) {
  return (
    <input type="number" defaultValue={value ?? ''} placeholder={placeholder || ''}
      onBlur={e => onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
      style={{ width: '100%' }} />
  );
}

function SectionHeader({ label }) {
  return (
    <div style={{gridColumn:'span 2',marginTop:12,marginBottom:6,
      fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.06em',
      color:'var(--brand)',borderBottom:'1px solid var(--border)',paddingBottom:4}}>
      {label}
    </div>
  );
}

const CONDITION = [['','— Select —'],['good','Good'],['fair','Fair'],['poor','Poor']];
const ACTION_NONE = [['none','None'],['repair','Repair'],['replace','Replace']];

export default function SystemsSection({ deal, onUpdate, statusLabel, statusColor }) {
  const [data, setData] = useState(deal.sow_systems || {});
  const [open, setOpen] = useState(false);

  const save = async (field, value) => {
    const updated = { ...data, [field]: value };
    setData(updated);
    const result = await api.updateSowSystems(deal.id, { [field]: value });
    // Server returns updated row (with auto-calc alimicon_cost)
    if (result && result.alimicon_cost !== undefined) {
      setData(d => ({ ...d, alimicon_cost: result.alimicon_cost }));
    }
  };

  const isAluminum = data.wiring_type === 'aluminum';
  const alimicon_cost = data.alimicon_cost || 0;

  const costLines = useMemo(() => {
    const all = computeSOWCosts({ ...deal, sow_systems: data });
    return all.filter(l => ['roof','plumbing','electrical','hvac','foundation'].includes(l.section));
  }, [deal, data]);

  const summaryParts = [
    data.roof_action && data.roof_action !== 'none' && `Roof: ${data.roof_action}`,
    data.plumbing_action && data.plumbing_action !== 'none' && `Plumbing: ${data.plumbing_action}`,
    data.panel_action && data.panel_action !== 'none' && `Panel: ${data.panel_action}`,
    isAluminum && 'Aluminum wiring',
    data.hvac_action && data.hvac_action !== 'none' && `HVAC: ${data.hvac_action}`,
    data.foundation_action && data.foundation_action !== 'none' && `Foundation: ${data.foundation_action}`,
  ].filter(Boolean);

  return (
    <div className="card" style={{marginBottom:16,padding:0,overflow:'hidden'}}>
      <div style={{display:'flex',alignItems:'center',gap:10,padding:'12px 16px',cursor:'pointer',
        borderLeft:'4px solid #8e44ad'}} onClick={() => setOpen(o => !o)}>
        <div style={{flex:1}}>
          <div style={{fontWeight:600,fontSize:14}}>Major Systems</div>
          <div style={{fontSize:12,color:'var(--text-muted)',marginTop:2}}>
            {summaryParts.length ? summaryParts.join(' · ') : 'Roof · Plumbing · Electrical · HVAC · Foundation'}
          </div>
        </div>
        {statusLabel && (
          <span style={{fontSize:11,fontWeight:600,padding:'2px 8px',borderRadius:12,
            background:`${statusColor}22`,color:statusColor,whiteSpace:'nowrap'}}>
            {statusLabel}
          </span>
        )}
        <span style={{color:'var(--text-muted)',fontSize:16}}>{open ? '▲' : '▼'}</span>
      </div>

      {open && (
        <div style={{padding:'16px',borderTop:'1px solid var(--bg)'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 20px'}}>

            {/* ── Roof ── */}
            <SectionHeader label="Roof" />
            <Field label="Roof Age (years)">
              <NumberInput value={data.roof_age} onChange={v => save('roof_age', v)} placeholder="10" />
            </Field>
            <Field label="Roof Type">
              <SELECT value={data.roof_type} onChange={v => save('roof_type', v)}
                options={[['','— Select —'],['shingle','Shingle'],['flat','Flat / TPO'],['metal','Metal'],['tile','Tile'],['other','Other']]} />
            </Field>
            <Field label="Condition">
              <SELECT value={data.roof_condition} onChange={v => save('roof_condition', v)} options={CONDITION} />
            </Field>
            <Field label="Action">
              <SELECT value={data.roof_action} onChange={v => save('roof_action', v)} options={ACTION_NONE} />
            </Field>
            {data.roof_action === 'replace' && (
              <Field label="Roof Sq Ft">
                <NumberInput value={data.roof_sq_ft} onChange={v => save('roof_sq_ft', v)} placeholder="1800" />
              </Field>
            )}
            <div style={{gridColumn:'span 2'}}>
              <Field label="Roof Notes">
                <input defaultValue={data.roof_notes || ''} onBlur={e => save('roof_notes', e.target.value)}
                  placeholder="Visible damage, layers, last replaced…" style={{width:'100%'}} />
              </Field>
            </div>

            {/* ── Plumbing ── */}
            <SectionHeader label="Plumbing" />
            <Field label="Supply Pipe Type">
              <SELECT value={data.supply_pipe_type} onChange={v => save('supply_pipe_type', v)}
                options={[['','— Select —'],['copper','Copper'],['cpvc','CPVC'],['pvc','PVC'],['pex','PEX'],['galvanized','Galvanized'],['other','Other']]} />
            </Field>
            <Field label="Sewer Pipe Type">
              <SELECT value={data.sewer_pipe_type} onChange={v => save('sewer_pipe_type', v)}
                options={[['','— Select —'],['cast_iron','Cast Iron'],['pvc','PVC'],['orangeburg','Orangeburg'],['clay','Clay'],['other','Other']]} />
            </Field>
            <Field label="Plumbing Condition">
              <SELECT value={data.plumbing_condition} onChange={v => save('plumbing_condition', v)} options={CONDITION} />
            </Field>
            <Field label="Plumbing Action">
              <SELECT value={data.plumbing_action} onChange={v => save('plumbing_action', v)}
                options={[['none','None'],['repair','Repair'],['repipe','Full Repipe']]} />
            </Field>
            <Field label="Water Heater Type">
              <SELECT value={data.wh_type} onChange={v => save('wh_type', v)}
                options={[['','— Select —'],['electric','Electric'],['gas','Gas'],['tankless','Tankless']]} />
            </Field>
            <Field label="WH Age (years)">
              <NumberInput value={data.wh_age} onChange={v => save('wh_age', v)} />
            </Field>
            <Field label="WH Condition">
              <SELECT value={data.wh_condition} onChange={v => save('wh_condition', v)} options={CONDITION} />
            </Field>
            <Field label="WH Action">
              <SELECT value={data.wh_action} onChange={v => save('wh_action', v)}
                options={[['keep','Keep'],['replace','Replace']]} />
            </Field>
            <div style={{gridColumn:'span 2'}}>
              <Field label="Plumbing Notes">
                <input defaultValue={data.plumbing_notes || ''} onBlur={e => save('plumbing_notes', e.target.value)}
                  placeholder="Known leaks, pressure issues…" style={{width:'100%'}} />
              </Field>
            </div>

            {/* ── Electrical ── */}
            <SectionHeader label="Electrical" />
            <Field label="Panel Amps">
              <SELECT value={data.panel_amps} onChange={v => save('panel_amps', parseInt(v) || null)}
                options={[['','— Select —'],['60','60A'],['100','100A'],['150','150A'],['200','200A'],['400','400A']]} />
            </Field>
            <Field label="Panel Brand">
              <SELECT value={data.panel_brand} onChange={v => save('panel_brand', v)}
                options={[['','— Select —'],['ge','GE'],['square_d','Square D'],['siemens','Siemens'],
                  ['cutler_hammer','Cutler-Hammer'],['federal_pacific','Federal Pacific ⚠'],['zinsco','Zinsco ⚠'],['other','Other']]} />
            </Field>
            <Field label="Panel Condition">
              <SELECT value={data.panel_condition} onChange={v => save('panel_condition', v)} options={CONDITION} />
            </Field>
            <Field label="Panel Action">
              <SELECT value={data.panel_action} onChange={v => save('panel_action', v)}
                options={[['none','None'],['upgrade','Upgrade Amps'],['replace','Replace Panel']]} />
            </Field>
            <Field label="Wiring Type">
              <SELECT value={data.wiring_type} onChange={v => save('wiring_type', v)}
                options={[['','— Select —'],['copper','Copper'],['cloth_ground','Cloth w/ Ground'],
                  ['cloth_no_ground','Cloth w/o Ground'],['aluminum','Aluminum ⚠']]} />
            </Field>
            {isAluminum && (
              <Field label="Alimicon Connectors Installed?">
                <SELECT value={data.alimicon_present ? 'yes' : 'no'} onChange={v => save('alimicon_present', v === 'yes' ? 1 : 0)}
                  options={[['no','No — must install'],['yes','Yes — already have']]} />
              </Field>
            )}
            {isAluminum && !data.alimicon_present && (
              <div style={{gridColumn:'span 2',background:'#fff3cd',border:'1px solid #ffc107',borderRadius:'var(--radius)',
                padding:'10px 14px',marginBottom:8,fontSize:12}}>
                ⚠ <strong>Alimicon required:</strong> Aluminum wiring with no connectors → est. cost auto-calculated from room outlets.
                {alimicon_cost > 0 && (
                  <span style={{marginLeft:8,fontWeight:700,color:'var(--danger)'}}>
                    ${alimicon_cost.toLocaleString()} (outlets × $75)
                  </span>
                )}
              </div>
            )}
            <div style={{gridColumn:'span 2'}}>
              <Field label="Electrical Notes">
                <input defaultValue={data.electrical_notes || ''} onBlur={e => save('electrical_notes', e.target.value)}
                  placeholder="GFCI, outdoor outlets, panel location…" style={{width:'100%'}} />
              </Field>
            </div>

            {/* ── HVAC ── */}
            <SectionHeader label="HVAC" />
            <Field label="System Age (years)">
              <NumberInput value={data.hvac_age} onChange={v => save('hvac_age', v)} />
            </Field>
            <Field label="Tons">
              <SELECT value={data.hvac_tons} onChange={v => save('hvac_tons', parseFloat(v) || null)}
                options={[['','— Select —'],['1.5','1.5 ton'],['2','2 ton'],['2.5','2.5 ton'],['3','3 ton'],
                  ['3.5','3.5 ton'],['4','4 ton'],['5','5 ton']]} />
            </Field>
            <Field label="HVAC Condition">
              <SELECT value={data.hvac_condition} onChange={v => save('hvac_condition', v)} options={CONDITION} />
            </Field>
            <Field label="HVAC Action">
              <SELECT value={data.hvac_action} onChange={v => save('hvac_action', v)}
                options={[['none','None'],['service','Service / Clean'],['replace','Replace']]} />
            </Field>
            <Field label="Ductwork Condition">
              <SELECT value={data.ductwork_condition} onChange={v => save('ductwork_condition', v)}
                options={[['','— Select —'],['good','Good'],['fair','Fair'],['poor','Poor'],['none','No Ductwork']]} />
            </Field>
            <div style={{gridColumn:'span 2'}}>
              <Field label="HVAC Notes">
                <input defaultValue={data.hvac_notes || ''} onBlur={e => save('hvac_notes', e.target.value)}
                  placeholder="Last serviced, known issues…" style={{width:'100%'}} />
              </Field>
            </div>

            {/* ── Foundation ── */}
            <SectionHeader label="Foundation" />
            <Field label="Foundation Type">
              <SELECT value={data.foundation_type} onChange={v => save('foundation_type', v)}
                options={[['','— Select —'],['slab','Slab'],['crawlspace','Crawlspace'],['pier_beam','Pier & Beam'],['basement','Basement']]} />
            </Field>
            <Field label="Condition">
              <SELECT value={data.foundation_condition} onChange={v => save('foundation_condition', v)} options={CONDITION} />
            </Field>
            <Field label="Action">
              <SELECT value={data.foundation_action} onChange={v => save('foundation_action', v)}
                options={[['none','None'],['monitor','Monitor'],['repair','Repair']]} />
            </Field>
            <div style={{gridColumn:'span 2'}}>
              <Field label="Foundation Notes">
                <input defaultValue={data.foundation_notes || ''} onBlur={e => save('foundation_notes', e.target.value)}
                  placeholder="Cracks, settling, moisture issues…" style={{width:'100%'}} />
              </Field>
            </div>

          </div>

          <SOWCostTable dealId={deal.id} lines={costLines} sowCosts={deal.sow_costs} onSynced={onUpdate} />
        </div>
      )}
    </div>
  );
}
